// src/components/BucksAdminCustomerManage.jsx

import React, { useState, useEffect, useRef } from 'react';
import '../stylings/BucksAdminCustomerManage.css';
import BucksAdminCustomerDetails from './BucksAdminCustomerDetails';

// Define API endpoints (replace with actual endpoints when available)
const GET_CUSTOMERS_URL = 'https://rv0kwp3fuc.execute-api.us-east-1.amazonaws.com/default/bucks-admin-customers-get';
const GET_SUBSCRIBERS_URL = 'https://rv0kwp3fuc.execute-api.us-east-1.amazonaws.com/default/bucks-admin-subscribers-get';
const GET_CONTACTS_URL = 'https://rv0kwp3fuc.execute-api.us-east-1.amazonaws.com/default/bucks-admin-customer-contact-forms-get';
const DELETE_CONTACT_URL = 'https://rv0kwp3fuc.execute-api.us-east-1.amazonaws.com/default/bucks-customer-contact-forms-delete';
const UPDATE_CUSTOMER_URL = 'https://rv0kwp3fuc.execute-api.us-east-1.amazonaws.com/default/bucks-admin-customer-update';
const EXPORT_CUSTOMERS_URL = 'https://rv0kwp3fuc.execute-api.us-east-1.amazonaws.com/default/bucks-admin-customers-export';

function BucksAdminCustomerManage() {
  /* ────────────────────── STATE ────────────────────── */
  // Active tab
  const [activeTab, setActiveTab] = useState('customers');
  
  // Data states
  const [customers, setCustomers] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  
  // Filters and search
  const [searchTerm, setSearchTerm] = useState('');
  const [customerTypeFilter, setCustomerTypeFilter] = useState('all');
  const [dateRange, setDateRange] = useState({
    from: '',
    to: ''
  });
  
  // UI states
  const [loading, setLoading] = useState({
    customers: true,
    subscribers: false,
    contacts: false
  });
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [detailsPanel, setDetailsPanel] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [customerUpdateData, setCustomerUpdateData] = useState({
    notes: '',
    tags: [],
    isSubscribed: false
  });
  
  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 10,
    total_count: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false
  });
  
  // Refs
  const searchTimer = useRef(null);
  const detailsPanelRef = useRef(null);
  
  /* ────────────────────── CONSTANTS ────────────────────── */
  const CUSTOMER_TYPES = [
    'All Customers',
    'Repeat Customers',
    'New Customers',
    'High Value'
  ];
  
  /* ────────────────────── EFFECTS ────────────────────── */
  // Fetch data based on active tab
  useEffect(() => {
    switch (activeTab) {
      case 'customers':
        fetchCustomers(pagination.page, searchTerm, customerTypeFilter, dateRange);
        break;
      case 'subscribers':
        fetchSubscribers(pagination.page, searchTerm, dateRange);
        break;
      case 'contacts':
        fetchContacts(pagination.page, searchTerm, dateRange);
        break;
      default:
        break;
    }
  }, [activeTab, pagination.page]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Close details panel when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (detailsPanel && detailsPanelRef.current && !detailsPanelRef.current.contains(event.target)) {
        setDetailsPanel(false);
      }
    }
    
    if (detailsPanel) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
    return () => {};
  }, [detailsPanel]);
  
  /* ────────────────────── API CALLS ────────────────────── */
  // Fetch customers from API
  const fetchCustomers = async (page = 1, search = '', type = 'all', dates = { from: '', to: '' }) => {
    setLoading(prev => ({ ...prev, customers: true }));
    try {
      // Build query parameters
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('page_size', pagination.page_size);
      
      if (search) {
        params.append('search', search);
      }
      
      if (type !== 'all') {
        params.append('type', type);
      }
      
      if (dates.from) {
        params.append('date_from', dates.from);
      }
      
      if (dates.to) {
        params.append('date_to', dates.to);
      }
      
      // For demo purposes, we'll create mock data instead of calling actual API
      // In production, uncomment the fetch code below
      
      /*
      const response = await fetch(`${GET_CUSTOMERS_URL}?${params.toString()}`);
      
      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }
      
      const data = await response.json();
      
      setCustomers(data.customers);
      setPagination(data.pagination);
      */
      
      // Mock data for development
      const mockData = generateMockCustomers();
      setCustomers(mockData.customers);
      setPagination({
        page: page,
        page_size: 10,
        total_count: mockData.customers.length,
        total_pages: Math.ceil(mockData.customers.length / 10),
        has_next: page < Math.ceil(mockData.customers.length / 10),
        has_prev: page > 1
      });
      
      // Simulate API delay
      setTimeout(() => {
        setLoading(prev => ({ ...prev, customers: false }));
      }, 500);
      
    } catch (error) {
      console.error('Error fetching customers:', error);
      setNotification({
        show: true,
        message: `Error loading customers: ${error.message}`,
        type: 'error'
      });
      setCustomers([]);
      setLoading(prev => ({ ...prev, customers: false }));
    }
  };
  
  // Fetch subscribers from API
  const fetchSubscribers = async (page = 1, search = '', dates = { from: '', to: '' }) => {
    setLoading(prev => ({ ...prev, subscribers: true }));
    try {
      // Build query parameters
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('page_size', pagination.page_size);
      
      if (search) {
        params.append('search', search);
      }
      
      if (dates.from) {
        params.append('date_from', dates.from);
      }
      
      if (dates.to) {
        params.append('date_to', dates.to);
      }
      
      // For demo purposes, we'll create mock data
      const mockData = generateMockSubscribers();
      setSubscribers(mockData.subscribers);
      setPagination({
        page: page,
        page_size: 10,
        total_count: mockData.subscribers.length,
        total_pages: Math.ceil(mockData.subscribers.length / 10),
        has_next: page < Math.ceil(mockData.subscribers.length / 10),
        has_prev: page > 1
      });
      
      // Simulate API delay
      setTimeout(() => {
        setLoading(prev => ({ ...prev, subscribers: false }));
      }, 500);
      
    } catch (error) {
      console.error('Error fetching subscribers:', error);
      setNotification({
        show: true,
        message: `Error loading subscribers: ${error.message}`,
        type: 'error'
      });
      setSubscribers([]);
      setLoading(prev => ({ ...prev, subscribers: false }));
    }
  };
  
  // Fetch contact submissions from API
  const fetchContacts = async (page = 1, search = '', dates = { from: '', to: '' }) => {
    setLoading(prev => ({ ...prev, contacts: true }));
    try {
      // Build query parameters
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('page_size', pagination.page_size);
      
      if (search) {
        params.append('search', search);
      }
      
      if (dates.from) {
        params.append('date_from', dates.from);
      }
      
      if (dates.to) {
        params.append('date_to', dates.to);
      }
      
      // Make API call
      const response = await fetch(`${GET_CONTACTS_URL}?${params.toString()}`);
      
      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }
      
      const data = await response.json();
      
      setContacts(data.contacts);
      setPagination(data.pagination);
      
      setLoading(prev => ({ ...prev, contacts: false }));
      
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setNotification({
        show: true,
        message: `Error loading contact submissions: ${error.message}`,
        type: 'error'
      });
      setContacts([]);
      setLoading(prev => ({ ...prev, contacts: false }));
    }
  };
  
  // Delete contact message
  const deleteContact = async (contactId) => {
    try {
      setLoading(prev => ({ ...prev, contacts: true }));
      
      const response = await fetch(`${DELETE_CONTACT_URL}?id=${contactId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }
      
      const data = await response.json();
      
      // Remove the deleted contact from the local state
      setContacts(prevContacts => prevContacts.filter(contact => contact.id !== contactId));
      
      // Close details panel if the deleted contact was selected
      if (selectedCustomer && selectedCustomer.id === contactId) {
        setDetailsPanel(false);
        setSelectedCustomer(null);
      }
      
      // Update pagination if needed
      setPagination(prev => ({
        ...prev,
        total_count: prev.total_count - 1,
        total_pages: Math.ceil((prev.total_count - 1) / prev.page_size)
      }));
      
      setNotification({
        show: true,
        message: 'Contact message deleted successfully.',
        type: 'success'
      });
      
      // Hide notification after 3 seconds
      setTimeout(() => {
        setNotification({ show: false, message: '', type: '' });
      }, 3000);
      
    } catch (error) {
      console.error('Error deleting contact:', error);
      setNotification({
        show: true,
        message: `Error deleting contact: ${error.message}`,
        type: 'error'
      });
    } finally {
      setLoading(prev => ({ ...prev, contacts: false }));
      setShowDeleteModal(false);
      setContactToDelete(null);
    }
  };

  // Handle delete confirmation
  const handleDeleteContact = (contact) => {
    setContactToDelete(contact);
    setShowDeleteModal(true);
  };

  // Confirm delete action
  const confirmDeleteContact = () => {
    if (contactToDelete) {
      deleteContact(contactToDelete.id);
    }
  };
  
  // Update customer data
  const updateCustomer = async (customerId, updateData) => {
    try {
      setLoading(prev => ({ ...prev, customers: true }));
      
      // In production, uncomment the fetch code below
      /*
      const response = await fetch(UPDATE_CUSTOMER_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': sessionStorage.getItem('adminToken')
        },
        body: JSON.stringify({
          customer_id: customerId,
          ...updateData
        })
      });
      
      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }
      
      const data = await response.json();
      */
      
      // For demo, update the local state directly
      const updatedCustomers = customers.map(customer => {
        if (customer.id === customerId) {
          return {
            ...customer,
            notes: updateData.notes,
            tags: updateData.tags,
            is_subscribed: updateData.isSubscribed,
            updated_at: new Date().toISOString()
          };
        }
        return customer;
      });
      
      setCustomers(updatedCustomers);
      
      // Update selected customer if it's open
      if (selectedCustomer && selectedCustomer.id === customerId) {
        setSelectedCustomer({
          ...selectedCustomer,
          notes: updateData.notes,
          tags: updateData.tags,
          is_subscribed: updateData.isSubscribed,
          updated_at: new Date().toISOString()
        });
      }
      
      setNotification({
        show: true,
        message: `Customer information has been updated successfully.`,
        type: 'success'
      });
      
      // Hide notification after 3 seconds
      setTimeout(() => {
        setNotification({ show: false, message: '', type: '' });
      }, 3000);
      
    } catch (error) {
      console.error('Error updating customer:', error);
      setNotification({
        show: true,
        message: `Error updating customer: ${error.message}`,
        type: 'error'
      });
    } finally {
      setLoading(prev => ({ ...prev, customers: false }));
      setShowUpdateModal(false);
    }
  };
  
  // Export customer data
  const exportCustomers = async () => {
    try {
      setNotification({
        show: true,
        message: "Preparing export file...",
        type: "success"
      });
      
      // In production, use fetch to get export file
      /*
      const response = await fetch(EXPORT_CUSTOMERS_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': sessionStorage.getItem('adminToken')
        },
        body: JSON.stringify({
          search: searchTerm,
          type: customerTypeFilter,
          date_from: dateRange.from,
          date_to: dateRange.to
        })
      });
      
      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'bucks_customers_export.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      */
      
      // For demo purposes, simulate successful export
      setTimeout(() => {
        setNotification({
          show: true,
          message: "Export completed! File downloaded.",
          type: "success"
        });
        
        setTimeout(() => {
          setNotification({ show: false, message: '', type: '' });
        }, 3000);
      }, 1500);
      
    } catch (error) {
      console.error('Error exporting customers:', error);
      setNotification({
        show: true,
        message: `Export failed: ${error.message}`,
        type: 'error'
      });
    }
  };
  
  /* ────────────────────── EVENT HANDLERS ────────────────────── */
  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchTerm('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };
  
  // Handle search input changes with debounce
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    // Debounce to avoid too many API calls while typing
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      switch (activeTab) {
        case 'customers':
          fetchCustomers(1, value, customerTypeFilter, dateRange);
          break;
        case 'subscribers':
          fetchSubscribers(1, value, dateRange);
          break;
        case 'contacts':
          fetchContacts(1, value, dateRange);
          break;
        default:
          break;
      }
    }, 500);
  };
  
  // Handle customer type filter changes
  const handleTypeChange = (e) => {
    const value = e.target.value;
    setCustomerTypeFilter(value);
    if (activeTab === 'customers') {
      fetchCustomers(1, searchTerm, value, dateRange);
    } else if (activeTab === 'contacts') {
      fetchContacts(1, searchTerm, dateRange);
    }
  };
  
  // Handle date range changes
  const handleDateChange = (e) => {
    const { name, value } = e.target;
    const newDateRange = { ...dateRange, [name]: value };
    setDateRange(newDateRange);
    
    // Only trigger search if both dates are set or both are empty
    if ((newDateRange.from && newDateRange.to) || (!newDateRange.from && !newDateRange.to)) {
      switch (activeTab) {
        case 'customers':
          fetchCustomers(1, searchTerm, customerTypeFilter, newDateRange);
          break;
        case 'subscribers':
          fetchSubscribers(1, searchTerm, newDateRange);
          break;
        case 'contacts':
          fetchContacts(1, searchTerm, newDateRange);
          break;
        default:
          break;
      }
    }
  };
  
  // Handle pagination
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };
  
  // Handle row click to open customer details
  const handleRowClick = (item) => {
    switch (activeTab) {
      case 'customers':
        setSelectedCustomer(item);
        setDetailsPanel(true);
        break;
      case 'subscribers':
        // Find if this subscriber is also a customer
        const customer = customers.find(c => c.email === item.email);
        if (customer) {
          setSelectedCustomer(customer);
        } else {
          // Create a customer-like object from subscriber data
          setSelectedCustomer({
            id: item.id,
            first_name: item.first_name,
            last_name: item.last_name,
            email: item.email,
            phone: '',
            address: {},
            orders: [],
            total_spent: 0,
            is_subscribed: true,
            subscribed_at: item.subscribed_at,
            tags: ['Newsletter'],
            notes: ''
          });
        }
        setDetailsPanel(true);
        break;
      case 'contacts':
        // Find if this contact is also a customer
        const contactCustomer = customers.find(c => c.email === item.email);
        if (contactCustomer) {
          setSelectedCustomer(contactCustomer);
        } else {
          // Create a customer-like object from contact data
          setSelectedCustomer({
            id: item.id,
            first_name: item.name.split(' ')[0],
            last_name: item.name.split(' ').slice(1).join(' '),
            email: item.email,
            phone: item.phone || '',
            address: {},
            orders: [],
            total_spent: 0,
            is_subscribed: false,
            tags: ['Contact Form'],
            notes: item.message
          });
        }
        setDetailsPanel(true);
        break;
      default:
        break;
    }
  };
  
  // Close customer details panel
  const closeCustomerDetails = () => {
    setDetailsPanel(false);
    // Wait for animation to complete before clearing the selected customer
    setTimeout(() => {
      setSelectedCustomer(null);
    }, 300);
  };
  
  // Open update modal
  const openUpdateModal = (customer) => {
    setSelectedCustomer(customer);
    setCustomerUpdateData({
      notes: customer.notes || '',
      tags: customer.tags || [],
      isSubscribed: customer.is_subscribed || false
    });
    setShowUpdateModal(true);
  };
  
  // Handle update modal form changes
  const handleUpdateFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setCustomerUpdateData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setCustomerUpdateData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  // Handle tag removal
  const handleRemoveTag = (tagToRemove) => {
    setCustomerUpdateData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };
  
  // Handle tag addition
  const handleAddTag = (e) => {
    if (e.key === 'Enter' && e.target.value.trim()) {
      const newTag = e.target.value.trim();
      if (!customerUpdateData.tags.includes(newTag)) {
        setCustomerUpdateData(prev => ({
          ...prev,
          tags: [...prev.tags, newTag]
        }));
      }
      e.target.value = '';
    }
  };
  
  // Handle update form submission
  const handleUpdateSubmit = (e) => {
    e.preventDefault();
    if (selectedCustomer) {
      updateCustomer(selectedCustomer.id, customerUpdateData);
    }
  };
  
  /* ────────────────────── UTILITY FUNCTIONS ────────────────────── */
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };
  
  // Format date with time
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };
  
  // Format currency
  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '$0.00';
    return `$${parseFloat(amount).toFixed(2)}`;
  };
  
  // Generate mock customers for development
  const generateMockCustomers = () => {
    const mockCustomers = [];
    const firstNames = ['John', 'Jane', 'Robert', 'Emily', 'Michael', 'Sarah', 'David', 'Lisa', 'William', 'Jessica'];
    const lastNames = ['Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis', 'Rodriguez', 'Martinez'];
    
    // Generate 20-35 customers
    const numCustomers = Math.floor(Math.random() * 15) + 20;
    
    for (let i = 1; i <= numCustomers; i++) {
      const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
      const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];
      const email = `${firstName.toLowerCase()}.${lastName.toLowerCase()}@example.com`;
      
      // Random data
      const registrationDate = new Date();
      registrationDate.setDate(registrationDate.getDate() - Math.floor(Math.random() * 365));
      
      const numOrders = Math.floor(Math.random() * 5);
      const totalSpent = numOrders * (Math.random() * 100 + 20);
      
      // Random tags
      const possibleTags = ['Repeat Customer', 'New Customer', 'High Value', 'Local', 'CSA Member', 'Event Attendee'];
      const numTags = Math.floor(Math.random() * 3);
      const tags = [];
      for (let j = 0; j < numTags; j++) {
        const tag = possibleTags[Math.floor(Math.random() * possibleTags.length)];
        if (!tags.includes(tag)) {
          tags.push(tag);
        }
      }
      
      // Randomly assign subscription status
      const isSubscribed = Math.random() > 0.4;
      
      mockCustomers.push({
        id: `C${10000 + i}`,
        first_name: firstName,
        last_name: lastName,
        email: email,
        phone: `555-${String(Math.floor(Math.random() * 900) + 100)}-${String(Math.floor(Math.random() * 9000) + 1000)}`,
        address: {
          street: `${Math.floor(Math.random() * 9000) + 1000} ${['Main', 'Oak', 'Maple', 'Washington', 'Cedar'][Math.floor(Math.random() * 5)]} St`,
          city: ['Farmville', 'Harvesttown', 'Greenfield', 'Riverside', 'Oak Forest'][Math.floor(Math.random() * 5)],
          state: 'IL',
          zipcode: `6${Math.floor(Math.random() * 10000).toString().padStart(4, '0')}`
        },
        registered_at: registrationDate.toISOString(),
        last_order_date: numOrders > 0 ? new Date(Date.now() - Math.floor(Math.random() * 30 * 24 * 60 * 60 * 1000)).toISOString() : null,
        num_orders: numOrders,
        total_spent: totalSpent,
        is_subscribed: isSubscribed,
        subscribed_at: isSubscribed ? new Date(Date.now() - Math.floor(Math.random() * 180 * 24 * 60 * 60 * 1000)).toISOString() : null,
        tags: tags,
        notes: Math.random() > 0.7 ? 'Customer prefers organic products.' : '',
        created_at: registrationDate.toISOString(),
        updated_at: registrationDate.toISOString()
      });
    }
    
    // Sort by registration date, newest first
    mockCustomers.sort((a, b) => new Date(b.registered_at) - new Date(a.registered_at));
    
    return { customers: mockCustomers };
  };
  
  // Generate mock subscribers for development
  const generateMockSubscribers = () => {
    const mockSubscribers = [];
    const firstNames = ['John', 'Jane', 'Robert', 'Emily', 'Michael', 'Sarah', 'David', 'Lisa', 'William', 'Jessica', 'Richard', 'Susan', 'Thomas', 'Karen', 'Daniel', 'Nancy'];
    const lastNames = ['Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis', 'Rodriguez', 'Martinez', 'Wilson', 'Anderson', 'Taylor', 'Thomas', 'Moore', 'Jackson'];
    
    // Generate 30-50 subscribers
    const numSubscribers = Math.floor(Math.random() * 20) + 30;
    
    for (let i = 1; i <= numSubscribers; i++) {
      const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
      const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];
      const email = `${firstName.toLowerCase()}.${lastName.toLowerCase()}@example.com`;
      
      // Subscribe date
      const subscribeDate = new Date();
      subscribeDate.setDate(subscribeDate.getDate() - Math.floor(Math.random() * 365));
      
      // Sources
      const sources = ['Website Form', 'Event Sign-up', 'Checkout Option', 'Manual Entry', 'Social Media'];
      const source = sources[Math.floor(Math.random() * sources.length)];
      
      mockSubscribers.push({
        id: `S${10000 + i}`,
        email: email,
        first_name: firstName,
        last_name: lastName,
        subscribed_at: subscribeDate.toISOString(),
        source: source,
        is_active: true,
        interests: Math.random() > 0.5 ? ['CSA Program', 'Seasonal Produce', 'Events'][Math.floor(Math.random() * 3)] : null
      });
    }
    
    // Sort by subscription date, newest first
    mockSubscribers.sort((a, b) => new Date(b.subscribed_at) - new Date(a.subscribed_at));
    
    return { subscribers: mockSubscribers };
  };
  
  /* ────────────────────── RENDER ────────────────────── */
  return (
    <div className="bucks-admin-customer-section">
      {/* Notification */}
      {notification.show && (
        <div className={`bucks-admin-customer-notification ${notification.type}`}>
          <span className="bucks-admin-customer-notification-message">{notification.message}</span>
          <button 
            className="bucks-admin-customer-close-notification" 
            onClick={() => setNotification({ show: false, message: '', type: '' })}
          >
            ×
          </button>
        </div>
      )}
      
      {/* Tabs */}
      <div className="bucks-admin-customer-tabs">
        <button 
          className={`bucks-admin-customer-tab ${activeTab === 'customers' ? 'active' : ''}`}
          onClick={() => handleTabChange('customers')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
            <circle cx="9" cy="7" r="4"></circle>
            <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
          </svg>
          All Customers
        </button>
        <button 
          className={`bucks-admin-customer-tab ${activeTab === 'subscribers' ? 'active' : ''}`}
          onClick={() => handleTabChange('subscribers')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
            <polyline points="22,6 12,13 2,6"></polyline>
          </svg>
          Newsletter Subscribers
        </button>
        <button 
          className={`bucks-admin-customer-tab ${activeTab === 'contacts' ? 'active' : ''}`}
          onClick={() => handleTabChange('contacts')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
          </svg>
          Contact Form Submissions
        </button>
      </div>
      
      {/* Header - Search and Filters */}
      <div className="bucks-admin-customer-actions">
        <div className="bucks-admin-customer-search-filter-container">
          <div className="bucks-admin-customer-search-container">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input
              type="text"
              placeholder={activeTab === 'customers' ? "Search customers by name, email or phone..." :
                          activeTab === 'subscribers' ? "Search subscribers by email or name..." :
                          "Search contact submissions by name or email..."}
              value={searchTerm}
              onChange={handleSearchChange}
              className="bucks-admin-customer-search-input"
            />
          </div>
          
          {activeTab === 'customers' && (
            <div className="bucks-admin-customer-filter-container">
              <div className="bucks-admin-customer-filter-group">
                <label htmlFor="customer-type-filter" className="bucks-admin-customer-filter-label">Customer Type:</label>
                <select
                  id="customer-type-filter"
                  value={customerTypeFilter}
                  onChange={handleTypeChange}
                  className="bucks-admin-customer-type-filter"
                >
                  <option value="all">All Customers</option>
                  <option value="repeat">Repeat Customers</option>
                  <option value="new">New Customers</option>
                  <option value="high-value">High Value</option>
                </select>
              </div>
              
              <div className="bucks-admin-customer-filter-group">
                <label className="bucks-admin-customer-filter-label">Registration Date:</label>
                <div className="bucks-admin-customer-date-inputs">
                  <input
                    type="date"
                    name="from"
                    value={dateRange.from}
                    onChange={handleDateChange}
                    className="bucks-admin-customer-date-input"
                    placeholder="From"
                  />
                  <span className="bucks-admin-customer-date-separator">to</span>
                  <input
                    type="date"
                    name="to"
                    value={dateRange.to}
                    onChange={handleDateChange}
                    className="bucks-admin-customer-date-input"
                    placeholder="To"
                  />
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'subscribers' && (
            <div className="bucks-admin-customer-filter-container">
              <div className="bucks-admin-customer-filter-group">
                <label className="bucks-admin-customer-filter-label">Subscription Date:</label>
                <div className="bucks-admin-customer-date-inputs">
                  <input
                    type="date"
                    name="from"
                    value={dateRange.from}
                    onChange={handleDateChange}
                    className="bucks-admin-customer-date-input"
                    placeholder="From"
                  />
                  <span className="bucks-admin-customer-date-separator">to</span>
                  <input
                    type="date"
                    name="to"
                    value={dateRange.to}
                    onChange={handleDateChange}
                    className="bucks-admin-customer-date-input"
                    placeholder="To"
                  />
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'contacts' && (
            <div className="bucks-admin-customer-filter-container">
              <div className="bucks-admin-customer-filter-group">
                <label className="bucks-admin-customer-filter-label">Status:</label>
                <select
                  value={customerTypeFilter}
                  onChange={handleTypeChange}
                  className="bucks-admin-customer-type-filter"
                >
                  <option value="all">All Status</option>
                  <option value="new">New</option>
                  <option value="read">In Progress</option>
                  <option value="archived">Completed</option>
                </select>
              </div>
              
              <div className="bucks-admin-customer-filter-group">
                <label className="bucks-admin-customer-filter-label">Submission Date:</label>
                <div className="bucks-admin-customer-date-inputs">
                  <input
                    type="date"
                    name="from"
                    value={dateRange.from}
                    onChange={handleDateChange}
                    className="bucks-admin-customer-date-input"
                    placeholder="From"
                  />
                  <span className="bucks-admin-customer-date-separator">to</span>
                  <input
                    type="date"
                    name="to"
                    value={dateRange.to}
                    onChange={handleDateChange}
                    className="bucks-admin-customer-date-input"
                    placeholder="To"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        
        <button 
          className="bucks-admin-customer-export-btn"
          onClick={exportCustomers}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7 10 12 15 17 10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
          Export {activeTab === 'customers' ? 'Customers' : 
                 activeTab === 'subscribers' ? 'Subscribers' : 'Contacts'}
        </button>
      </div>
      
      {/* Main Content */}
      {loading[activeTab] ? (
        <div className="bucks-admin-customer-loading">
          <div className="bucks-admin-customer-loading-spinner"></div>
          <span>Loading {activeTab}...</span>
        </div>
      ) : (
        <>
          {/* Customers Table */}
          {activeTab === 'customers' && (
            <div className="bucks-admin-customer-table-container">
              <table className="bucks-admin-customer-table">
                <thead>
                  <tr>
                    <th>Customer ID</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Registration Date</th>
                    <th>Orders</th>
                    <th>Total Spent</th>
                    <th>Subscribed</th>
                    <th>Tags</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.length > 0 ? (
                    customers.map(customer => (
                      <tr 
                        key={customer.id}
                        className="bucks-admin-customer-clickable-row"
                        onClick={() => handleRowClick(customer)}
                      >
                        <td>{customer.id}</td>
                        <td>
                          <div className="bucks-admin-customer-name">
                            {customer.first_name} {customer.last_name}
                          </div>
                        </td>
                        <td>{customer.email}</td>
                        <td>{customer.phone || 'N/A'}</td>
                        <td>{formatDate(customer.registered_at)}</td>
                        <td>{customer.num_orders}</td>
                        <td>{formatCurrency(customer.total_spent)}</td>
                        <td>
                          <span className={`bucks-admin-customer-subscription-badge ${customer.is_subscribed ? 'subscribed' : 'not-subscribed'}`}>
                            {customer.is_subscribed ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td>
                          <div className="bucks-admin-customer-tags">
                            {customer.tags && customer.tags.map((tag, index) => (
                              <span key={index} className="bucks-admin-customer-tag">{tag}</span>
                            ))}
                          </div>
                        </td>
                        <td onClick={e => e.stopPropagation()}>
                          <div className="bucks-admin-customer-actions-cell">
                            <button 
                              className="bucks-admin-customer-view-btn" 
                              onClick={() => handleRowClick(customer)}
                              title="View Customer Details"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                <circle cx="12" cy="12" r="3"></circle>
                              </svg>
                            </button>
                            <button 
                              className="bucks-admin-customer-edit-btn" 
                              onClick={() => openUpdateModal(customer)}
                              title="Edit Customer"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="10" className="bucks-admin-customer-no-results">
                        No customers found matching your search criteria
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Subscribers Table */}
          {activeTab === 'subscribers' && (
            <div className="bucks-admin-customer-table-container">
              <table className="bucks-admin-customer-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Name</th>
                    <th>Subscribed Date</th>
                    <th>Source</th>
                    <th>Interests</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {subscribers.length > 0 ? (
                    subscribers.map(subscriber => (
                      <tr 
                        key={subscriber.id}
                        className="bucks-admin-customer-clickable-row"
                        onClick={() => handleRowClick(subscriber)}
                      >
                        <td>{subscriber.email}</td>
                        <td>
                          {subscriber.first_name && subscriber.last_name 
                            ? `${subscriber.first_name} ${subscriber.last_name}`
                            : 'N/A'}
                        </td>
                        <td>{formatDate(subscriber.subscribed_at)}</td>
                        <td>{subscriber.source || 'Website'}</td>
                        <td>{subscriber.interests || 'General'}</td>
                        <td>
                          <span className={`bucks-admin-customer-status-badge ${subscriber.is_active ? 'active' : 'inactive'}`}>
                            {subscriber.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td onClick={e => e.stopPropagation()}>
                          <div className="bucks-admin-customer-actions-cell">
                            <button 
                              className="bucks-admin-customer-view-btn" 
                              onClick={() => handleRowClick(subscriber)}
                              title="View Subscriber Details"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                <circle cx="12" cy="12" r="3"></circle>
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="bucks-admin-customer-no-results">
                        No subscribers found matching your search criteria
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Contact Submissions Table */}
          {activeTab === 'contacts' && (
            <div className="bucks-admin-customer-table-container">
              <table className="bucks-admin-customer-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Subject</th>
                    <th>Submission Date</th>
                    <th>Status</th>
                    <th>Assigned To</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.length > 0 ? (
                    contacts.map(contact => (
                      <tr 
                        key={contact.id}
                        className="bucks-admin-customer-clickable-row"
                        onClick={() => handleRowClick(contact)}
                      >
                        <td>{contact.id}</td>
                        <td>{contact.name}</td>
                        <td>{contact.email}</td>
                        <td>{contact.phone || 'N/A'}</td>
                        <td>{contact.subject}</td>
                        <td>{formatDateTime(contact.submitted_at)}</td>
                        <td>
                          <span className={`bucks-admin-customer-status-badge status-${contact.status.toLowerCase().replace(' ', '-')}`}>
                            {contact.status}
                          </span>
                        </td>
                        <td>{contact.assigned_to || 'Unassigned'}</td>
                        <td onClick={e => e.stopPropagation()}>
                          <div className="bucks-admin-customer-actions-cell">
                            <button 
                              className="bucks-admin-customer-view-btn" 
                              onClick={() => handleRowClick(contact)}
                              title="View Contact Details"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                <circle cx="12" cy="12" r="3"></circle>
                              </svg>
                            </button>
                            <button 
                              className="bucks-admin-customer-delete-btn" 
                              onClick={() => handleDeleteContact(contact)}
                              title="Delete Contact"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3,6 5,6 21,6"></polyline>
                                <path d="M19,6l-1,14a2,2,0,0,1-2,2H8a2,2,0,0,1-2-2L5,6m5,0V4a2,2,0,0,1,2-2h0a2,2,0,0,1,2,2V6"></path>
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="bucks-admin-customer-no-results">
                        No contact form submissions found matching your search criteria
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="bucks-admin-customer-pagination">
              <button 
                className="bucks-admin-customer-pagination-btn" 
                disabled={!pagination.has_prev}
                onClick={() => handlePageChange(pagination.page - 1)}
              >
                Previous
              </button>
              <span className="bucks-admin-customer-pagination-info">
                Page {pagination.page} of {pagination.total_pages}
              </span>
              <button 
                className="bucks-admin-customer-pagination-btn" 
                disabled={!pagination.has_next}
                onClick={() => handlePageChange(pagination.page + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
      
      {/* Customer Details Side Panel - Using a separate component to clean up this file */}
      {detailsPanel && selectedCustomer && (
        <div ref={detailsPanelRef}>
          <BucksAdminCustomerDetails
            customer={selectedCustomer}
            isOpen={detailsPanel}
            onClose={closeCustomerDetails}
            onUpdate={openUpdateModal}
            onDelete={selectedCustomer.message ? handleDeleteContact : null}
            formatDate={formatDate}
            formatDateTime={formatDateTime}
            formatCurrency={formatCurrency}
          />
        </div>
      )}
      
      {/* Customer Update Modal */}
      {showUpdateModal && selectedCustomer && (
        <div className="bucks-admin-customer-modal-overlay" onClick={() => setShowUpdateModal(false)}>
          <div className="bucks-admin-customer-modal-container" onClick={e => e.stopPropagation()}>
            <div className="bucks-admin-customer-modal-header">
              <h2>Update Customer - {selectedCustomer.first_name} {selectedCustomer.last_name}</h2>
              <button 
                className="bucks-admin-customer-modal-close" 
                onClick={() => setShowUpdateModal(false)}
                aria-label="Close modal"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleUpdateSubmit} className="bucks-admin-customer-modal-form">
              <div className="bucks-admin-customer-modal-form-group">
                <label htmlFor="customer-notes">Customer Notes</label>
                <textarea
                  id="customer-notes"
                  name="notes"
                  value={customerUpdateData.notes}
                  onChange={handleUpdateFormChange}
                  placeholder="Add notes about this customer"
                  rows="4"
                ></textarea>
              </div>
              
              <div className="bucks-admin-customer-modal-form-group">
                <label>Tags</label>
                <div className="bucks-admin-customer-tags-container">
                  {customerUpdateData.tags.map((tag, index) => (
                    <div key={index} className="bucks-admin-customer-tag-item">
                      <span>{tag}</span>
                      <button 
                        type="button"
                        className="bucks-admin-customer-tag-remove"
                        onClick={() => handleRemoveTag(tag)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <input
                    type="text"
                    className="bucks-admin-customer-tag-input"
                    placeholder="Add a tag and press Enter"
                    onKeyPress={handleAddTag}
                  />
                </div>
              </div>
              
              <div className="bucks-admin-customer-modal-form-group">
                <div className="bucks-admin-customer-checkbox-group">
                  <input
                    type="checkbox"
                    id="is-subscribed"
                    name="isSubscribed"
                    checked={customerUpdateData.isSubscribed}
                    onChange={handleUpdateFormChange}
                  />
                  <label htmlFor="is-subscribed">Subscribed to Newsletter</label>
                </div>
              </div>
              
              <div className="bucks-admin-customer-modal-actions">
                <button 
                  type="button" 
                  className="bucks-admin-customer-modal-cancel" 
                  onClick={() => setShowUpdateModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="bucks-admin-customer-modal-submit"
                  disabled={loading.customers}
                >
                  {loading.customers ? (
                    <>
                      <span className="spinner"></span>
                      <span>Updating...</span>
                    </>
                  ) : (
                    'Update Customer'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal && contactToDelete && (
        <div className="bucks-admin-customer-modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="bucks-admin-customer-modal-container" onClick={e => e.stopPropagation()}>
            <div className="bucks-admin-customer-modal-header">
              <h2>Delete Contact Message</h2>
              <button 
                className="bucks-admin-customer-modal-close" 
                onClick={() => setShowDeleteModal(false)}
                aria-label="Close modal"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            
            <div className="bucks-admin-customer-modal-form">
              <div className="bucks-admin-customer-delete-warning">
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                  <line x1="12" y1="9" x2="12" y2="13"></line>
                  <line x1="12" y1="17" x2="12.01" y2="17"></line>
                </svg>
                <h3>Are you sure you want to delete this contact message?</h3>
                <p>
                  This will permanently delete the message from <strong>{contactToDelete.name}</strong> 
                  with subject "<strong>{contactToDelete.subject}</strong>". This action cannot be undone.
                </p>
              </div>
              
              <div className="bucks-admin-customer-modal-actions">
                <button 
                  type="button" 
                  className="bucks-admin-customer-modal-cancel" 
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="bucks-admin-customer-modal-delete"
                  onClick={confirmDeleteContact}
                  disabled={loading.contacts}
                >
                  {loading.contacts ? (
                    <>
                      <span className="spinner"></span>
                      <span>Deleting...</span>
                    </>
                  ) : (
                    'Delete Message'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BucksAdminCustomerManage;